/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
/* eslint-disable no-mixed-operators */
const { serialnumber: { date2serial, dateLocal2serial, serial2date } } = require('@cedalo/commons');
const Point = require('../../geometry/Point');
const MathUtils = require('../../geometry/MathUtils');
const NumberExpression = require('../expr/NumberExpression');
const StringExpression = require('../expr/StringExpression');
const BooleanExpression = require('../expr/BooleanExpression');
const Numbers = require('../../commons/Numbers');
const Arrays = require('../../commons/Arrays');
const SheetReference = require('../parser/SheetReference');
const Cell = require('./Cell');
const SheetName = require('./SheetName');
const CellRange = require('./CellRange');

const TYPE = 'Data';

/**
 * Generic data provider for a worksheet. This provider simply saves its cell data in row
 * arrays. Each row array contains an array of cell arrays.
 *
 * @class DataProvider
 * @constructor
 */
module.exports = class DataProvider {
	constructor(sheet) {
		this._rows = [];
		this._sheet = sheet;
		this._names = [];
	}

	newInstance() {
		return new DataProvider();
	}

	copy() {
		const copy = this.newInstance();
		let i;

		for (i = 0; i < this._rows.length; i += 1) {
			copy._rows[i] = this.copyRow(this._rows[i]);
		}

		return copy;
	}

	copyRow(row) {
		let i;

		if (row === undefined) {
			return undefined;
		}

		const newRow = [];

		for (i = 0; i < row.length; i += 1) {
			if (row[i] !== undefined) {
				newRow[i] = row[i].copy();
			}
		}

		return newRow;
	}

	validateName(name) {
		if (name.length === 0) {
			return 'Invalid Name';
		}

		let ret = true;

		this._names.forEach((lname) => {
			if (lname.getName() === name) {
				ret = 'Name exists';
			}
		});

		return ret;
	}

	getType() {
		return DataProvider.TYPE;
	}

	get sheet() {
		return this._sheet;
	}

	get names() {
		return this._names;
	}

	getNames() {
		return this._names;
	}

	getOrCreateName(name) {
		const nameObj = this._names.find((lname) => lname.getName() === name);
		return nameObj || this.addName(new SheetName(name));
	}

	getName(name) {
		return this._names.find((lname) => lname.getName() === name);
	}

	addName(name) {
		this._names.push(name);
		name.evaluate(this._sheet);
		return name;
	}

	deleteName(name) {
		Arrays.remove(this._names, name);
	}

	get(pos) {
		return this.getRC(pos.x, pos.y);
	}

	clear() {
		this._rows = [];
	}

	getRC(column, row) {
		const rowdata = this.getRow(row);
		return rowdata ? rowdata[column] : undefined;
	}

	getUsedRange() {
		const range = new CellRange(this._sheet);
		let i;
		let j;
		const n = this.sheet.maxRows;
		let row;

		for (i = 0; i < n; i += 1) {
			row = this.getRow(i);
			if (row !== undefined) {
				if (range.getY1() === undefined) {
					range.setY1(i);
				}

				for (j = 0; j < row.length; j += 1) {
					if (row[j] !== undefined) {
						if (range.getX1() === undefined) {
							range.setX1(j);
						} else {
							range.setX1(Math.min(j, range.getX1()));
						}
						if (range.getX2() === undefined) {
							range.setX2(j);
						} else {
							range.setX2(Math.max(j, range.getX2()));
						}
					}
				}
				range.setY2(i);
			}
		}

		return range;
	}

	setArray(data) {
		const pos = new Point(0, 0);

		data.forEach((row, rowIndex) => {
			pos.y = rowIndex;
			row.forEach((ldata, columnIndex) => {
				pos.x = columnIndex;
				this.setString(pos, ldata ? ldata.toString() : '');
			});
		});
	}

	hasRow(row) {
		return this.getRow(row) !== undefined;
	}

	getRows() {
		return this._rows;
	}

	setRows(rows) {
		this._rows = rows;
	}

	getRowSize(index) {
		const row = this._rows[index];
		if (row === undefined) {
			return 0;
		}
		return row.length;
	}

	getRow(row) {
		return this._rows[row];
	}

	setRow(index, row) {
		this._rows[index] = row;
	}

	createRC(column, row) {
		return this.create({ x: column, y: row });
	}

	create(pos) {
		let rowdata = this.getRow(pos.y);
		if (!rowdata) {
			rowdata = [];
			this.setRow(pos.y, rowdata);
		}

		let cell = rowdata[pos.x];
		if (!cell) {
			cell = new Cell();
			rowdata[pos.x] = cell;
		}

		return cell;
	}

	setRC(x, y, expression, format) {
		const pos = new Point(x, y);
		return this.set(pos, expression, format);
	}

	setString(pos, text) {
		let data;

		if (text.toLowerCase() === 'true') {
			data = new BooleanExpression(true);
		} else if (text.toLowerCase() === 'false') {
			data = new BooleanExpression(false);
		} else if (Numbers.canBeNumber(text)) {
			data = new NumberExpression(text);
		} else {
			data = new StringExpression(text);
		}

		this.set(pos, data);
	}

	set(pos, expression, format) {
		let rowdata = this._rows[pos.y];
		if (!rowdata) {
			rowdata = [];
			this._rows[pos.y] = rowdata;
		}

		let cell = rowdata[pos.x];
		if (!cell) {
			cell = new Cell();
		}

		if (expression !== undefined) {
			cell.setExpression(expression);
		}

		if (format !== undefined) {
			cell.setFormat(format);
		}

		this.setTo(pos, cell);

		return cell;
	}

	// supports setting expression to undefined in order to remove it
	setExpression(pos, expression) {
		this._rows[pos.y] = this._rows[pos.y] || [];
		const rowdata = this._rows[pos.y];
		const cell = rowdata[pos.x] || new Cell();
		cell.setExpression(expression);
		this.setTo(pos, cell);
		return cell;
	}

	setRCTo(x, y, cell) {
		const pos = new Point(x, y);

		this.setTo(pos, cell);
	}

	setTo(pos, cell) {
		let rowdata = this.getRow(pos.y);
		if (!rowdata) {
			if (!cell) {
				return;
			}
			rowdata = [];
			this._rows[pos.y] = rowdata;
		}

		rowdata[pos.x] = cell;
	}

	addRow(row) {
		this._rows.push(row);
	}

	remove(cell) {
		const rowdata = this._rows[cell.y];
		if (rowdata) {
			rowdata[cell.x] = undefined;
		}
	}

	markUpdate() {
		this.enumerate((column, row, cell) => {
			cell._updated = false;
		});
	}

	clearNotUpdated() {
		this.enumerate((column, row, cell) => {
			if (cell._updated === false) {
				cell.clearContent();
			}
			cell._updated = undefined;
		});
	}

	clearContent() {
		this.enumerate((column, row, cell) => {
			cell.clearContent();
		});
	}

	clearFormats() {
		this.enumerate((column, row, cell) => {
			cell.setFormat(undefined);
			cell.setTextFormat(undefined)
		});
	}

	/**
	 * Enumerate all cells.
	 * @param {function(column, row, cell)} callback Callback that is called for each existing cell.
	 */
	enumerate(callback) {
		let i;
		let j;
		let m;
		const n = this.sheet.maxRows;
		let rowdata;

		for (i = 0; i < n; i += 1) {
			rowdata = this._rows[i];
			if (rowdata) {
				for (j = 0, m = rowdata.length; j < m; j += 1) {
					const coldata = rowdata[j];
					if (coldata !== undefined) {
						if (callback(j, i, coldata) === false) {
							return;
						}
					}
				}
			}
		}
	}

	/**
	 * Enumerate cells in given column. Only used cells are enumerated.
	 * @param {Number} column Column to enumerate.
	 * @param {function} callback Function to call for cells. Passed arguments are the row index and the cell.
	 */
	enumerateColumn(column, callback) {
		let i;
		const n = this.sheet.maxRows;
		let rowdata;

		for (i = 0; i < n; i += 1) {
			rowdata = this.getRow(i);
			if (rowdata) {
				const coldata = rowdata[column];
				if (coldata !== undefined) {
					if (callback(i, coldata) === false) {
						return;
					}
				}
			}
		}
	}

	evaluate() {
		// if (item && item.getAttributeAtPath('range')) {
		// 	return;
		// }

		this.enumerate((c, r, cell) => {
			cell.evaluate(this._sheet);
			return true;
		});

		this._names.forEach((name) => {
			name.evaluate(this._sheet);
		});
	}

	invalidateTerms() {
		this.enumerate((c, r, cell) => {
			cell.invalidateTerm();
			return true;
		});

		this._names.forEach((name) => {
			name.invalidateTerm();
		});
	}

	save(writer) {
		writer.writeStartElement('data');
		writer.writeStartArray('r');

		const sheet = this.sheet;
		const rowCount = sheet.maxRows;
		const columnCount = sheet.maxColumns;
		let cancel;

		this._rows.forEach((rowdata, rowIndex) => {
			if (rowdata && rowIndex < rowCount) {
				writer.writeStartElement('r');
				writer.writeAttributeNumber('n', rowIndex, 0);
				writer.writeStartArray('c');
				cancel = true;
				rowdata.forEach((coldata, colIndex) => {
					if (coldata !== undefined && colIndex < columnCount && coldata.saveNeeded(writer)) {
						writer.writeStartElement('c');
						writer.writeAttributeNumber('n', colIndex, 0);
						coldata.save(writer);
						writer.writeEndElement();
						cancel = false;
					}
				});
				writer.writeEndArray('c');
				writer.writeEndElement(cancel);
			}
		});

		writer.writeEndArray('r');

		writer.writeEndElement();
	}

	read(reader, object) {
		reader.iterateObjects(object, (name, child) => {
			switch (name) {
				case 'r': {
					const row = Number(reader.getAttribute(child, 'n'));
					const rowData = [];
					let hasCell = false;
					reader.iterateObjects(child, (lname, grandchild) => {
						switch (lname) {
							case 'c': {
								const column = Number(reader.getAttribute(grandchild, 'n'));
								const cellNode = reader.getObject(grandchild, 'cell');
								if (cellNode !== undefined) {
									const cellData = new Cell();
									cellData.read(reader, cellNode);
									if (cellData.hasContent() || cellData.hasFormat()) {
										rowData[column] = cellData;
										hasCell = true;
									}
								}
								break;
							}
							default:
								break;
						}
					});
					if (hasCell) {
						this._rows[row] = rowData;
					}
					break;
				}
				default:
					break;
			}
		});
	}

	/** NOTE: respects local timezone. usually it is better to use dateToSerial instead */
	JSDateToExcelDate(inDate) {
		return dateLocal2serial(inDate);
	}

	dateToSerial(inDate) {
		return date2serial(inDate);
	}

	excelDateToJSDate(serial) {
		return serial2date(serial);
	}

	getCellValueSeries(source, target, selection) {
		const horizontal = target.getX2() !== source.getX2();
		let cell;
		let cells;
		let cellsAlpha;
		let val;
		let sum;
		let sumAlpha;
		let lastVal;
		let firstVal;
		let lastValAlpha;
		let firstValAlpha;
		const sections = [];
		const up = horizontal ? source.getX1() < target.getX1() : source.getY1() < target.getY1();

		for (
			let i = horizontal ? source.getY1() : source.getX1();
			i <= (horizontal ? source.getY2() : source.getX2());
			i += 1
		) {
			cells = 0;
			cellsAlpha = 0;
			sum = 0;
			sumAlpha = 0;
			lastVal = undefined;
			lastValAlpha = undefined;
			let monthDiff;
			let lastMonthDiff;
			let noDate = false;
			for (
				let j = horizontal ? source.getX1() : source.getY1();
				j <= (horizontal ? source.getX2() : source.getY2());
				j += 1
			) {
				let pos;
				if (horizontal) {
					pos = { x: j, y: i };
				} else {
					pos = { x: i, y: j };
				}
				cell = this.get(pos);
				if (cell) {
					const expr = cell.getExpression();
					if (expr && !expr.hasFormula()) {
						val = cell.getValue();
						if (Numbers.isNumber(val)) {
							const attr = this.sheet.getTextFormatProperties(pos.x, pos.y, true).numberformat;
							const date = attr && attr.length && attr.indexOf('date') !== -1;
							if (date) {
								const dateVal = this.excelDateToJSDate(val);
								if (lastVal !== undefined) {
									const lastDateVal = this.excelDateToJSDate(lastVal);
									lastMonthDiff = monthDiff;
									if (dateVal.getDate() === lastDateVal.getDate()) {
										monthDiff =
											dateVal.getMonth() -
											lastDateVal.getMonth() +
											(dateVal.getFullYear() - lastDateVal.getFullYear()) * 12;
										if (lastMonthDiff && lastMonthDiff !== monthDiff) {
											noDate = true;
										}
									}
								}
							}
							if (lastVal !== undefined) {
								sum += val - lastVal;
							}
							lastVal = val;
							if (firstVal === undefined) {
								firstVal = val;
							}
							cells += 1;
						} else if (typeof val === 'string') {
							const number = Number((val.match(/\d+$/) || []).pop());
							if (Numbers.isNumber(number)) {
								if (lastValAlpha !== undefined) {
									sumAlpha += number - lastValAlpha;
								}
								lastValAlpha = number;
								if (firstValAlpha === undefined) {
									firstValAlpha = number;
								}
								cellsAlpha += 1;
							}
						}
					}
				}
			}
			sections[i] = {
				step: cells > 1 ? sum / (cells - 1) : 0,
				final: lastVal,
				first: firstVal,
				stepAlpha: cellsAlpha > 1 ? sumAlpha / (cellsAlpha - 1) : 1,
				finalAlpha: lastValAlpha,
				firstAlpha: firstValAlpha,
				monthDiff: noDate === false ? monthDiff : undefined
			};
		}

		const cellData = [];

		sections.forEach((section, index) => {
			let repeat;
			let repeatAlpha;

			if (horizontal) {
				repeat = up ? 1 : target.getWidth();
			} else {
				repeat = up ? 1 : target.getHeight();
			}
			repeatAlpha = repeat;

			for (
				let i = horizontal ? target.getX1() : target.getY1();
				i <= (horizontal ? target.getX2() : target.getY2());
				i += 1
			) {
				let pos;
				if (horizontal) {
					pos = { x: source.getX1() + ((i - target.getX1()) % source.getWidth()), y: index };
				} else {
					pos = { x: index, y: source.getY1() + ((i - target.getY1()) % source.getHeight()) };
				}
				cell = this.get(pos);
				if (cell) {
					const expr = cell.getExpression();
					if (expr && !expr.hasFormula()) {
						val = cell.getValue();
						if (Numbers.isNumber(val)) {
							let set = false;
							if (section.monthDiff) {
								const attr = this.sheet.getTextFormatProperties(pos.x, pos.y, true).localculture;
								const date = attr && attr.length && attr.indexOf('date') !== -1;
								if (date) {
									const dateVal = this.excelDateToJSDate(val);
									if (up) {
										const finalDateVal = this.excelDateToJSDate(section.final);
										if (finalDateVal.getDate() === dateVal.getDate()) {
											set = true;
											dateVal.setMonth(
												(finalDateVal.getMonth() + section.monthDiff * repeat) % 12
											);
											dateVal.setFullYear(
												finalDateVal.getFullYear() +
													Math.floor(finalDateVal.getMonth() + section.monthDiff * repeat) /
														12
											);
											val = this.dateToSerial(dateVal);
											// val = this.JSDateToExcelDate(dateVal);
											repeat += 1;
										}
									} else {
										const firstDateVal = this.excelDateToJSDate(section.first);
										if (firstDateVal.getDate() === dateVal.getDate()) {
											set = true;
											dateVal.setMonth(
												(firstDateVal.getMonth() - section.monthDiff * repeat) % 12
											);
											dateVal.setFullYear(
												firstDateVal.getFullYear() +
													Math.floor(firstDateVal.getMonth() - section.monthDiff * repeat) /
														12
											);
											val = this.dateToSerial(dateVal);
											// val = this.JSDateToExcelDate(dateVal);
											repeat -= 1;
										}
									}
								}
							}
							if (!set) {
								if (up) {
									val = section.final + section.step * repeat;
									repeat += 1;
								} else {
									val = section.first - section.step * repeat;
									repeat -= 1;
								}
							}
							val = MathUtils.roundTo(val, 10);
						} else if (typeof val === 'string') {
							const number = (val.match(/\d+$/) || []).pop();
							if (number !== undefined && number.length) {
								let newValue;
								val = val.substring(0, val.length - number.length);
								if (up) {
									newValue = section.finalAlpha + section.stepAlpha * repeatAlpha;
									repeatAlpha += 1;
								} else {
									newValue = section.firstAlpha - section.stepAlpha * repeatAlpha;
									repeatAlpha -= 1;
								}
								newValue = MathUtils.roundTo(newValue, 10);
								val += newValue;
							}
						} else {
							val = undefined;
						}
						if (val !== undefined) {
							const data = {};
							if (horizontal) {
								data.reference = selection.cellToString(i, index);
							} else {
								data.reference = selection.cellToString(index, i);
							}
							data.value = val;
							cellData.push(data);
						}
					}
				}
			}
		});

		return cellData;
	}

	static get TYPE() {
		return TYPE;
	}
};
